/**
 * Hardcoded "Built-in capabilities · ask in natural language" section of the
 * ``/?`` discovery listing, for channels whose capability set the backend
 * does not yet project (see ``capability_listing.py``'s ``BUILT_IN_GROUPS``,
 * which currently only covers ``word``).
 *
 * Skills stay entirely dynamic — persona-scoped, fetched from the existing
 * ``GET /api/v1/skills/capabilities/{channel}`` /
 * ``GET /api/v1/skills/slash-commands/{channel}`` endpoints, unchanged by this
 * module. What is hardcoded here is only the fixed, persona-independent list
 * of things the add-in itself can always do on a channel — there is nothing
 * to fetch for that, and the Outlook feature specification (§4.2) is explicit
 * that every one of these entries is "deliberately not /-firable": the
 * listing exists purely to teach the user that plain language works.
 *
 * Consumed by ``useChat.ts``'s discovery-listing renderers, which merge this
 * in only when the fetched listing carries no ``builtin_``-prefixed group
 * already (i.e. only for channels the backend hasn't taken over) — so a
 * channel added here has no effect once the backend starts supplying its own
 * built-in groups, and nothing here duplicates or fights the server-owned
 * Word taxonomy.
 */
import type { Channel } from "../../shared/host";
import type { CapabilityGroupDTO } from "../services/api";

interface BuiltInCapabilityGroup {
  readonly key: string;
  readonly title: string;
  readonly items: readonly string[];
}

// Outlook feature specification, §4.2, Figure 4.1 — verbatim group titles and
// bullet text, in display order.
//
// ONE deliberate departure from verbatim: Figure 4.1 words the third mailbox
// entry "Surface unresolved actions in the inbox", which is the one thing MS-03's
// own boundary forbids the product from saying — "it is not an inbox-wide sweep …
// copy must not imply otherwise". The two passages contradict each other, and the
// boundary is the one stating intent, so the promise is scoped to what the
// capability does. Changing the behaviour to match the figure is not an option:
// there is no standing index of the mailbox to sweep (AIVA-7017).
const OUTLOOK_BUILT_INS: readonly BuiltInCapabilityGroup[] = Object.freeze([
  {
    key: "builtin_drafting",
    title: "Drafting & Replying",
    items: [
      "Draft a reply to the open email",
      "Compose a new email from a brief",
      "Adjust tone or formality of a draft",
      "Suggest follow-ups or next steps",
    ],
  },
  {
    key: "builtin_mailbox",
    title: "Mailbox Search & Triage",
    items: [
      "Find emails by sender, subject or content",
      "Summarise long email threads",
      "Surface unresolved actions in the open conversation",
    ],
  },
  {
    key: "builtin_calendar",
    title: "Calendar & Meetings",
    items: [
      "Find a free slot and book a meeting",
      "Generate a meeting agenda",
      "Convert email content into calendar events",
    ],
  },
  {
    key: "builtin_tasks",
    title: "Tasks & Follow-ups",
    items: ["Extract action items from emails", "Create tasks from email content"],
  },
]);

// Only channels the backend does not already project built-ins for. Word is
// deliberately absent — it is server-owned (see the module docstring).
const HARDCODED_BUILT_INS: Partial<Record<Channel, readonly BuiltInCapabilityGroup[]>> = Object.freeze({
  outlook: OUTLOOK_BUILT_INS,
});

/**
 * The hardcoded built-in capability groups for ``channel``, shaped as
 * ``CapabilityGroupDTO[]`` so callers can splice them straight into a fetched
 * (or locally built) listing's ``groups`` array. Every item's ``slash`` is
 * ``""`` — these are never ``/``-firable — matching how the backend's own
 * built-in items are shaped.
 *
 * Returns ``[]`` for a channel with no hardcoded entry (Word, PowerPoint, or
 * an unrecognised value) rather than throwing — an unmapped channel should
 * degrade to "skills only", not break ``/?`` for everyone on it.
 */
export function getHardcodedBuiltInGroups(channel: string | null): readonly CapabilityGroupDTO[] {
  const groups = channel ? HARDCODED_BUILT_INS[channel as Channel] : undefined;
  if (!groups) return [];
  return groups.map((group) => ({
    key: group.key,
    title: group.title,
    items: group.items.map((text) => ({ slash: "", label: text, description: text })),
  }));
}
